import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import {
  FaArrowLeft,
  FaEdit,
  FaFileDownload,
  FaFilter,
  FaPlus,
  FaSearch,
  FaSyncAlt,
  FaTable,
  FaTachometerAlt,
  FaTrash,
  FaTools,
} from "react-icons/fa";
import * as XLSX from "xlsx";
import moment from "moment-timezone";
import "./BreakdownTracker1.css";
import "./DfmsTruckDefectsTracker.css";

const normalizeRecord = (record) => {
  const normalized = {};
  Object.keys(record || {}).forEach((key) => {
    normalized[key.toLowerCase()] = record[key];
  });
  return normalized;
};

const DfmsTruckDefectsTracker = () => {
  const [defects, setDefects] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState("OPEN");
  const [searchInput, setSearchInput] = useState("");
  const [globalFilter, setGlobalFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const gridRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => setGlobalFilter(searchInput), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const fetchDefects = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_API_URL}/dfms-truck-defects`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const rows = Array.isArray(response.data)
        ? response.data
        : response.data?.data || [];
      setDefects(rows.map(normalizeRecord));
    } catch (error) {
      console.error("Error fetching DFMS truck defects:", error);
      setDefects([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDefects();
  }, [fetchDefects]);

  const handleDeleteDefect = useCallback(async (id) => {
    if (!id) return;
    if (!window.confirm("Delete this DFMS defect? This action cannot be undone.")) return;

    try {
      const token = localStorage.getItem("token");
      await axios.delete(
        `${process.env.REACT_APP_BASE_API_URL}/dfms-truck-defects/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchDefects();
      window.alert("DFMS defect deleted.");
    } catch (error) {
      console.error("Error deleting DFMS defect:", error);
      window.alert(error.response?.data?.message || "Failed to delete DFMS defect.");
    }
  }, [fetchDefects]);

  const filteredDefects = useMemo(() => {
    return defects
      .filter((defect) =>
        selectedStatus ? (defect.defect_status || "OPEN") === selectedStatus : true
      )
      .filter((defect) =>
        globalFilter
          ? Object.values(defect)
              .join(" ")
              .toLowerCase()
              .includes(globalFilter.toLowerCase())
          : true
      );
  }, [defects, selectedStatus, globalFilter]);

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return moment(date).format("DD/MM/YYYY HH:mm");
  };

  const dateFilterParams = useMemo(
    () => ({
      comparator: (filterLocalDateAtMidnight, cellValue) => {
        if (!cellValue) return -1;
        const cellDate = new Date(cellValue);
        if (Number.isNaN(cellDate.getTime())) return -1;
        const filterDate = new Date(filterLocalDateAtMidnight);
        const cellOnlyDate = new Date(
          cellDate.getFullYear(),
          cellDate.getMonth(),
          cellDate.getDate()
        );
        if (cellOnlyDate < filterDate) return -1;
        if (cellOnlyDate > filterDate) return 1;
        return 0;
      },
    }),
    []
  );

  const columnDefs = useMemo(
    () => [
      { headerName: "JP Number", field: "journey_plane_no", minWidth: 130 },
      { headerName: "Truck Number", field: "scheduled_vehicle", minWidth: 140 },
      {
        headerName: "Defect Time",
        field: "defect_time",
        minWidth: 170,
        filter: "agDateColumnFilter",
        filterParams: dateFilterParams,
        valueFormatter: ({ value }) => formatDateTime(value),
        sort: "desc",
      },
      { headerName: "Driver Name", field: "driver_name", minWidth: 160 },
      { headerName: "Driver Phone", field: "driver_phone", minWidth: 140 },
      { headerName: "Carrier", field: "carrier", minWidth: 120 },
      { headerName: "Last Check Point", field: "ivms_point", minWidth: 140 },
      { headerName: "Source", field: "source", minWidth: 130 },
      {
        headerName: "Remarks",
        field: "remarks",
        minWidth: 220,
        flex: 1.4,
        valueFormatter: ({ value }) => value || "N/A",
      },
      {
        headerName: "Status",
        field: "defect_status",
        minWidth: 130,
        cellRenderer: ({ value }) => (
          <div className="bt-status-badge">{value || "OPEN"}</div>
        ),
      },
      {
        headerName: "Date Resolved",
        field: "date_resolved",
        minWidth: 170,
        filter: "agDateColumnFilter",
        filterParams: dateFilterParams,
        valueFormatter: ({ value }) => formatDateTime(value),
      },
      {
        headerName: "Actions",
        field: "actions",
        minWidth: 130,
        filter: false,
        sortable: false,
        pinned: "right",
        cellRenderer: ({ data }) => {
          const id = data.dfms_defect_id || data.id;
          return (
            <div className="bt-action-icons">
              <button
                className="bt-action-btn bt-edit-btn"
                title="Edit"
                onClick={() => navigate(`/edit-dfms-truck-defect/${id}`)}
              >
                <FaEdit />
              </button>
              <button
                className="bt-action-btn bt-handle-btn"
                title="Resolve"
                onClick={() => navigate(`/handle-dfms-truck-defect/${id}`)}
              >
                <FaTools />
              </button>
              <button
                className="bt-action-btn bt-delete-btn"
                title="Delete"
                onClick={() => handleDeleteDefect(id)}
              >
                <FaTrash />
              </button>
            </div>
          );
        },
      },
    ],
    [dateFilterParams, handleDeleteDefect, navigate]
  );

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      sortable: true,
      filter: true,
      floatingFilter: false,
    }),
    []
  );

  const resetTableFilters = () => {
    setSearchInput("");
    setGlobalFilter("");
    gridRef.current?.api?.setFilterModel(null);
    gridRef.current?.api?.applyColumnState({ defaultState: { sort: null } });
  };

  const getExportRows = () => {
    const rows = [];
    const api = gridRef.current?.api;
    if (!api) return filteredDefects;
    api.forEachNodeAfterFilterAndSort((node) => {
      if (node.data) rows.push(node.data);
    });
    return rows;
  };

  const handleExport = () => {
    const rowsToExport = getExportRows();
    if (!rowsToExport.length) {
      window.alert("No DFMS defect records match the current filters.");
      return;
    }
    const exportData = rowsToExport.map((defect) => ({
      "JP Number": defect.journey_plane_no,
      "Truck Number": defect.scheduled_vehicle,
      "Defect Time": formatDateTime(defect.defect_time),
      "Driver Name": defect.driver_name,
      "Driver Phone": defect.driver_phone || "N/A",
      Carrier: defect.carrier,
      "Last Check Point": defect.ivms_point || "N/A",
      Source: defect.source || "N/A",
      Remarks: defect.remarks || "N/A",
      Status: defect.defect_status || "OPEN",
      "Date Resolved": formatDateTime(defect.date_resolved),
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "DFMS Defects");
    XLSX.writeFile(wb, `DFMS_Defects_${new Date().toISOString().split("T")[0]}.xlsx`);
  };

  const hasData = filteredDefects.length > 0;

  return (
    <div className="bt-container dfms-tracker-root">
      <div className="bt-header">
        <div className="bt-header-main">
          <div className="bt-header-left">
            <button className="bt-back-btn" onClick={() => navigate("/main")} title="Back to Main Page">
              <FaArrowLeft />
            </button>
            <div className="bt-header-title">
              <h1>DFMS Tracker</h1>
            </div>
          </div>
          <div className="bt-header-actions">
            <div className="bt-filter-group">
              <FaFilter className="bt-filter-icon" />
              <select value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)} className="bt-status-filter">
                <option value="">All Status</option>
                <option value="OPEN">OPEN</option>
                <option value="RESOLVED">RESOLVED</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="bt-main-content">
        <div className="bt-action-bar">
          <div className="bt-action-left">
            <button onClick={() => navigate("/add-dfms-truck-defect")} className="bt-primary-btn">
              <FaPlus /> Add DFMS Defect
            </button>
          </div>
          <div className="bt-action-right">
            <div className="bt-search-box">
              <FaSearch className="bt-search-icon" />
              <input type="text" placeholder="Search records..." value={searchInput} onChange={(e) => setSearchInput(e.target.value)} className="bt-search-input" />
            </div>
            <button className="bt-icon-btn" onClick={resetTableFilters} title="Reset table filters" type="button">
              <FaSyncAlt />
            </button>
            <button className="bt-export-btn" onClick={handleExport} disabled={!hasData}>
              <FaFileDownload /> Export Excel
            </button>
          </div>
        </div>

        <div className="bt-table-section">
          <div className="bt-table-container">
            {loading ? (
              <div className="bt-empty-state">Loading DFMS defects...</div>
            ) : hasData ? (
              <div className="ag-theme-alpine bt-ag-grid">
                <AgGridReact ref={gridRef} rowData={filteredDefects} columnDefs={columnDefs} defaultColDef={defaultColDef} rowHeight={30} headerHeight={36} pagination paginationPageSize={40} animateRows />
              </div>
            ) : (
              <div className="bt-empty-state">
                <FaTachometerAlt className="bt-empty-icon" />
                <h3>No DFMS defect records found</h3>
                <p>Try adjusting your search or add a new DFMS defect record</p>
                <button onClick={() => navigate("/add-dfms-truck-defect")} className="bt-primary-btn">
                  <FaPlus /> Add First DFMS Defect
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DfmsTruckDefectsTracker;
